import ComplaintScopeFilters, { appendOriginParams } from '../../components/ComplaintScopeFilters';
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Rating } from 'primereact/rating';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { handleGetRequest } from '../../service/GetTemplate';
import { endPoints } from '../../Config';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { formatTimeFromMinutes } from '../../utilities/formatTime';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const formatDateKey = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const parseDateKey = (value) => {
    if (!DATE_KEY_PATTERN.test(String(value || ''))) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (
        Number.isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) return null;
    return date;
};
const getDefaultClosedRange = () => {
    const today = new Date();
    return {
        from: formatDateKey(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: formatDateKey(today)
    };
};

const CloseComplaints = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [complaintList, setComplaintList] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const listRequest = useRef(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const origin = searchParams.get('origin') || '';
    const selectedTeam = searchParams.get('teamId') || '';
    const setScope = (key, value) => { const next = new URLSearchParams(searchParams); if (value) next.set(key, value); else next.delete(key); setSearchParams(next); };
    const defaultRange = getDefaultClosedRange();
    const closedFrom = parseDateKey(searchParams.get('from'))
        ? searchParams.get('from')
        : defaultRange.from;
    const closedTo = parseDateKey(searchParams.get('to'))
        ? searchParams.get('to')
        : defaultRange.to;
    const fromDate = parseDateKey(closedFrom);
    const toDate = parseDateKey(closedTo);

    const user = JSON.parse(localStorage.getItem('userDetails'));

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigateToChat = (rowData) => {
        navigate('/complaints/chat', {
            state: {
                complaint: rowData,
                returnTo: `${location.pathname}${location.search}`
            }
        });
    };

    const chatBodyTemplate = (rowData) => {
        return (
            <Button
                label={t('Open Chat')}
                icon="pi pi-comments"
                className="p-button-rounded p-button-sm"
                tooltip={t('Open chat with this complain')}
                tooltipOptions={{ position: 'top' }}
                style={{ backgroundColor: '#2563EB', border: 'none' }}
                onClick={() => handleNavigateToChat(rowData)}
            />
        );
    };

    const getComplaintList = async () => {
        const request = ++listRequest.current;
        setLoading(true);
        const adminUser = user?.data?.adminUser;
        const role = adminUser?.role || {};
        const roleUid = role?.uid;
        const teamId = adminUser?.teamId;

        const isSupport = roleUid === 'support';

        const params = new URLSearchParams();
        params.append('status', 'close');
        params.append('closedFrom', closedFrom);
        params.append('closedTo', closedTo);
        appendOriginParams(params, origin);
        if (!isSupport && selectedTeam) params.set('teamId', selectedTeam);

        // Only send teamId for support users; no userId/roleId filters
        if (isSupport && teamId) {
            params.append('teamId', teamId);
        }

        try {
            const response = await handleGetRequest(endPoints.getallComplaints(params.toString()), true, false, dispatch);
            if (request !== listRequest.current) return;
            // Add machineId field for searchability
            const complaintsWithMachineId = (response?.data || []).map(complaint => ({
                ...complaint,
                machineId: complaint.machine?.serialNumber || complaint.machine?.deviceId || '',
                refundBankName: complaint?.refund?.bankName || ''
            }));
            setComplaintList(complaintsWithMachineId);
        } finally {
            if (request === listRequest.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!searchParams.get('from') || !searchParams.get('to')) {
            const next = new URLSearchParams(searchParams);
            next.set('from', closedFrom);
            next.set('to', closedTo);
            setSearchParams(next, { replace: true });
        }
        getComplaintList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closedFrom, closedTo, origin, selectedTeam]);

    const updateClosedRange = ({ from = closedFrom, to = closedTo }) => {
        const next = new URLSearchParams(searchParams);
        if (from) next.set('from', from);
        else next.delete('from');
        if (to) next.set('to', to);
        else next.delete('to');
        setSearchParams(next);
    };

    const statusBody = (rowData) => {
        const rawStatus = (rowData?.status || '').toLowerCase();
        const mapLabel = {
            open: t('Open'),
            in_progress: t('Finance'),
            awaiting_customer: t('Awaiting Customer'),
            close: t('Closed'),
            forwarded: t('Forwarded'),
            pending: t('Pending')
        };
        const mapSeverity = {
            open: 'success',
            in_progress: 'info',
            awaiting_customer: 'warning',
            close: 'danger',
            forwarded: 'info',
            pending: 'warning'
        };
        const label = mapLabel[rawStatus] || rawStatus;
        const severity = mapSeverity[rawStatus] || 'info';

        return (
            <div className="flex justify-content-center">
                <Tag value={label} severity={severity} className="px-3 py-1 text-xs border-round-lg" />
            </div>
        );
    };
    const createdDateAndTimeBodyTemplate = (rowData) => {
        const isoDate = rowData?.createdAt;
        const date = new Date(isoDate);

        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yyyy = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');

        let ampm = 'AM';
        if (hours >= 12) {
            ampm = 'PM';
            hours -= 12;
        }
        if (hours === 0) {
            hours = 12;
        }

        const formattedDateTime = `${dd}/${mm}/${yyyy} ${hours}:${minutes} ${ampm}`;
        return <p>{formattedDateTime}</p>;
    };

    // Calculate time elapsed between open and close
    const calculateTimeElapsed = (createdAt, closedAt) => {
        if (!createdAt || !closedAt) return null;
        const created = new Date(createdAt);
        const closed = new Date(closedAt);
        const diffMs = closed - created;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes;
    };

    const timeElapsedBodyTemplate = (rowData) => {
        const minutes = calculateTimeElapsed(rowData?.createdAt, rowData?.closedAt);
        if (minutes === null) return '-';
        const formatted = formatTimeFromMinutes(minutes);
        return <span>{formatted}</span>;
    };

    const userRatingBodyTemplate = (rowData) => {
        const rating = rowData?.ratting;
        if (!rating || rating === 0) return <span className="text-500">{t('No rating')}</span>;
        return (
            <div className="flex align-items-center gap-2">
                <Rating value={rating} readOnly cancel={false} stars={5} />
                <span className="text-sm">({rating}/5)</span>
            </div>
        );
    };
    return (
        <div className="p-4">
            <p className="text-600">Cerrados durante el período seleccionado, aunque se hayan creado en meses anteriores. Fechas de cierre en horario de Panamá. Se excluyen conversaciones provisionales y abandonadas.</p>
            <ComplaintScopeFilters id="closed" origin={origin} onOriginChange={value => setScope('origin', value)} teamId={selectedTeam} onTeamChange={value => setScope('teamId', value)} lockedTeamId={user?.data?.adminUser?.role?.uid === 'support' ? user?.data?.adminUser?.teamId : ''} />
            <div className="flex justify-content-between align-items-center mb-3">
                <h3>{t('Close Complaints')}</h3>

                <div className="flex gap-2 flex-wrap justify-content-end">
                    <Calendar
                        aria-label="Fecha de cierre desde"
                        value={fromDate}
                        onChange={(event) => updateClosedRange({ from: formatDateKey(event.value) })}
                        dateFormat="yy-mm-dd"
                        placeholder={t('Closed from')}
                        showIcon
                        maxDate={toDate || undefined}
                    />
                    <Calendar
                        aria-label="Fecha de cierre hasta"
                        value={toDate}
                        onChange={(event) => updateClosedRange({ to: formatDateKey(event.value) })}
                        dateFormat="yy-mm-dd"
                        placeholder={t('Closed to')}
                        showIcon
                        minDate={fromDate || undefined}
                    />
                    <Button
                        type="button"
                        label={t('This month')}
                        icon="pi pi-calendar"
                        className="p-button-outlined"
                        disabled={closedFrom === defaultRange.from && closedTo === defaultRange.to}
                        onClick={() => updateClosedRange(defaultRange)}
                    />
                    <span className="p-input-icon-left w-full" style={{ maxWidth: '300px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder={t('Search complaints...')}
                            className="w-full"
                        />
                    </span>
                </div>
            </div>

            <DataTable
                paginator
                rows={15}
                rowsPerPageOptions={[5, 10, 20]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                emptyMessage={t('No List found.')}
                responsiveLayout="scroll"
                value={complaintList}
                loading={loading}
                globalFilter={globalFilter}
                className="p-datatable-sm datatable-responsive"
            >
                <Column 
                    header={t('Machine ID')} 
                    body={(rowData) => rowData.machine?.serialNumber || rowData.machine?.deviceId || '-'}
                    field="machineId"
                />
                <Column header={t('Location')} field="machine.location" />
                <Column
                    header={t('User')}
                    body={(rowData) => {
                        return rowData?.user?.firstName + ' ' + rowData?.user?.lastName;
                    }}
                />
                <Column header={t('WhatsApp Number')} field="whatsAppNumber" />
                <Column header={t('Bank Entity')} body={(rowData) => rowData?.refundBankName || '-'} field="refundBankName" />
                <Column header={t('Date')} body={createdDateAndTimeBodyTemplate} />
                <Column header={t('Time Elapsed')} body={timeElapsedBodyTemplate} />
                <Column header={t('User Rating')} body={userRatingBodyTemplate} />
                <Column header={t('Status')} body={statusBody} />
                <Column header={t('Chat')} body={chatBodyTemplate} />
            </DataTable>
        </div>
    );
};

export default CloseComplaints;
